<template>
  <ion-page>
    <ion-header class="ion-no-border">
      <ion-toolbar>
        <ion-title class="ion-text-center">Welcome Back</ion-title>
      </ion-toolbar>
    </ion-header>

    <ion-content class="ion-padding">
      <div class="login-container">
        <div class="logo-container">
          <img 
            :src="logoSrc" 
            alt="CreditEdge Logo" 
            class="logo"
          >
        </div>

        <!-- Login Form -->
        <form @submit.prevent="handleLogin">
          <ion-item>
            <ion-input 
              v-model="email" 
              type="email" 
              required
              placeholder="Email"
            ></ion-input>
          </ion-item>

          <ion-item>
            <ion-input 
              v-model="password" 
              type="password" 
              required
              placeholder="Password"
            ></ion-input>
          </ion-item>

          <ion-button 
            type="submit" 
            expand="block"
            class="submit-button"
            :disabled="!email || !password || loading"
          >
            {{ loading ? 'Signing in...' : 'Sign In' }}
          </ion-button>

          <div class="ion-text-center ion-padding-top">
            <ion-button fill="clear" @click="showForgotPassword = true">
              Forgot Password?
            </ion-button>
          </div>
          
          <div class="ion-text-center ion-padding-top disclaimer-text">
            <p>Contact your administrator if you need an account.</p>
          </div>
        </form>

        <!-- Password Reset Form -->
        <form v-if="showForgotPassword" @submit.prevent="handlePasswordReset" class="password-reset-form">
          <div class="ion-text-center">
            <h2>Reset Password</h2>
            <p>Enter your email address and we'll send you a link to reset your password.</p>
          </div>
          
          <ion-item>
            <ion-input 
              v-model="resetEmail" 
              type="email" 
              required
              placeholder="Email"
            ></ion-input>
          </ion-item>

          <ion-button 
            type="submit" 
            expand="block"
            class="submit-button"
            :disabled="!resetEmail || resetLoading"
          >
            {{ resetLoading ? 'Sending...' : 'Send Reset Link' }}
          </ion-button>

          <div class="ion-text-center ion-padding-top">
            <ion-button fill="clear" @click="showForgotPassword = false">
              Back to Login
            </ion-button>
          </div>
        </form>
      </div>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { supabase } from '@/supabase'
import { useRouter } from 'vue-router'
import { 
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent,
  IonItem, IonLabel, IonInput, IonButton, toastController 
} from '@ionic/vue'
import { useUserStore } from '@/store/user'
import { useToast } from '@/composables/useToast'
import creditEdgeLogo from '@/assets/creditEdgeLogo.svg'
import { Preferences } from '@capacitor/preferences'
import { Capacitor } from '@capacitor/core'
import { firebaseService } from '@/services/firebaseService'
import { FCM } from '@capacitor-community/fcm';
import { PushNotifications } from '@capacitor/push-notifications';

const router = useRouter()
const email = ref('')
const password = ref('')
const loading = ref(false)
const showForgotPassword = ref(false)
const resetEmail = ref('')
const resetLoading = ref(false)
const { setUser } = useUserStore()
const { showToast } = useToast()

const isDarkMode = ref(window.matchMedia('(prefers-color-scheme: dark)').matches)

// Watch for theme changes
const updateTheme = (e: MediaQueryListEvent) => {
  isDarkMode.value = e.matches
}
onUnmounted(() => {
  window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', updateTheme)
})

// Always use light logo (black text version) regardless of system theme
const logoSrc = computed(() => creditEdgeLogo)

// Add storage utility
const Storage = {
  async get(key: string): Promise<string | null> {
    if (Capacitor.isNativePlatform()) {
      const { value } = await Preferences.get({ key });
      return value;
    } else {
      return localStorage.getItem(key);
    }
  },

  async set(key: string, value: string): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await Preferences.set({ key, value });
    } else {
      localStorage.setItem(key, value);
    }
  },

  async remove(key: string): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await Preferences.remove({ key });
    } else {
      localStorage.removeItem(key);
    }
  }
};

// Add storage key
const STORAGE_KEY = 'login_form_data';

// Load saved form data on mount
onMounted(async () => {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', updateTheme)


  try {
    const savedData = await Storage.get(STORAGE_KEY);
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      email.value = parsedData.email || '';
      password.value = parsedData.password || '';
    }
  } catch (e) {
    console.error('Error loading saved form data:', e);
  }
});

// Watch for form changes and save
watch([email, password], async () => {
  try {
    await Storage.set(STORAGE_KEY, JSON.stringify({
      email: email.value,
      password: password.value
    }));
  } catch (e) {
    console.error('Error saving form data:', e);
  }
}, { deep: true });

// Clear storage after successful login
const clearStoredFormData = async () => {
  try {
    await Storage.remove(STORAGE_KEY);
  } catch (e) {
    console.error('Error clearing form data:', e);
  }
};

const handleLogin = async () => {
  try {
    loading.value = true;
    
    // Dismiss keyboard if active
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement?.blur) {
      activeElement.blur();
    }
    
    // Show loading indicator
    const loadingIndicator = await toastController.create({
      message: 'Signing in...',
      duration: 0,
      position: 'top',
      cssClass: 'custom-gray-toast',
      color: 'medium'
    });
    await loadingIndicator.present();

    // Attempt sign in
    const { data: { user }, error } = await supabase.auth.signInWithPassword({
      email: email.value,
      password: password.value,
    });

    if (error) throw error;

    console.log('Login successful, user ID:', user?.id);

    try {
      // external required step
      // register for push
      await PushNotifications.requestPermissions();
      await PushNotifications.register();
      
      // Get and handle FCM token
      const tokenResult = await FCM.getToken();
      console.log('FCM Token received:', tokenResult.token);
      
      // Insert token into database
      const { error: tokenError } = await supabase
        .from('fcm_tokens')
        .upsert({ 
          profile_pk: user?.id, 
          fcm_token: tokenResult.token 
        });
        
      if (tokenError) {
        console.error('Error inserting FCM token:', tokenError);
      } else {
        console.log('FCM token successfully inserted');
      }
    }
    catch (e) {
      console.error('Error in push notification setup:', e);
    }
    


    try {
      // Fetch user's profile data including rooftop_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user?.id)
        .single();

      if (profileError) throw profileError;

      // Set the complete user data including rooftop_id
      setUser({
        id: user?.id || '',
        username: profile.username || '',
        isAdmin: profile.is_admin || false,
        isSuperAdmin: profile.is_super_admin || false,
        isOwner: profile.is_owner || false,
        rooftop_id: profile.rooftop_id || '',
        full_name: '',
        avatar_url: '',
        updated_at: ''
      });


      // Dismiss loading indicator
      await loadingIndicator.dismiss();

      // Clear stored form data after successful login
      await clearStoredFormData();
      
      // Clear form data
      email.value = '';
      password.value = '';

      // Navigate after successful login
      await router.replace('/tabs/tab1');

    } catch (profileError: any) {
      console.error('Error fetching profile:', profileError);
      // Sign out on profile fetch error to prevent partial authentication state
      await supabase.auth.signOut();
      throw new Error('Unable to load user profile. Please try again.');
    }

  } catch (error: any) {
    console.error('Error:', error.message);
    await showToast({
      message: error.error_description || error.message,
      color: 'danger'
    });
  } finally {
    loading.value = false;
  }
}

const handlePasswordReset = async () => {
  try {
    resetLoading.value = true;
    
    // Dismiss keyboard if active
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement?.blur) {
      activeElement.blur();
    }
    
    // Show loading indicator
    const loadingIndicator = await toastController.create({
      message: 'Sending...',
      duration: 0,
      position: 'top',
      cssClass: 'custom-gray-toast',
      color: 'medium'
    });
    await loadingIndicator.present();

    // Attempt password reset
    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail.value, {
      // Specify the redirect URL to ensure PKCE flow works correctly
      redirectTo: `${window.location.origin}/reset-password`
    });

    if (error) throw error;

    console.log('Password reset email sent');

    // Dismiss loading indicator
    await loadingIndicator.dismiss();

    // Clear form data
    resetEmail.value = '';

    // Show success message
    const toast = await toastController.create({
      message: 'Password reset link sent to your email',
      duration: 3000,
      color: 'success'
    });
    await toast.present();

  } catch (error: any) {
    console.error('Error:', error.message);
    await showToast({
      message: error.error_description || error.message,
      color: 'danger'
    });
  } finally {
    resetLoading.value = false;
  }
}
</script>

<style scoped>
ion-page {
  --background: #F5F5F7 !important;
  background: #F5F5F7 !important;
}

ion-header {
  background: #F5F5F7;
}

ion-toolbar {
  --background: #F5F5F7;
  --border-style: none;
  border-bottom: none;
}

ion-content {
  --background: #F5F5F7;
}

ion-content::part(scroll) {
  background: #F5F5F7 !important;
}

.login-container {
  max-width: 400px;
  margin: 0 auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  background: #F5F5F7;
}

.logo-container {
  text-align: center;
  padding-top: 40px;
}

.logo {
  width: 180px;
  height: auto;
  margin-bottom: 40px;
}

form {
  margin-top: 40px;
  width: 100%;
}

ion-title {
  color: #000000 !important;
  font-weight: 600;
}

ion-item {
  --background: #F2F2F7;
  --border-radius: 12px;
  --padding-start: 16px;
  --padding-end: 16px;
  --highlight-height: 0;
  margin-bottom: 16px;
  --border-width: 0;
  --min-height: 60px;
  border: none !important;
  box-shadow: none !important;
}

ion-input {
  --color: #000000;
  --placeholder-color: #8E8E93;
  --background: transparent;
  --caret-color: #000000 !important;
  --highlight-color: #000000 !important;
  font-size: 18px;
  margin: 8px 0;
  --padding-top: 12px;
  --padding-bottom: 12px;
}

ion-input::part(native) {
  min-height: 24px;
  caret-color: #000000 !important;
  -webkit-text-fill-color: #000000 !important;
}

.submit-button,
ion-button[type="submit"] {
  --background: #1C1C1E;
  --background-activated: #2C2C2E;
  --background-hover: #2C2C2E;
  --color: #FFFFFF;
  --border-radius: 12px;
  height: 48px;
  font-weight: 600;
  margin: 24px 0;
  transition: all 0.2s ease;
}

.submit-button:hover,
ion-button[type="submit"]:hover {
  --background: #2C2C2E;
  transform: translateY(-1px);
}

ion-button[fill="clear"] {
  --color: #0A84FF;
  font-weight: 500;
  opacity: 0.9;
  transition: opacity 0.2s ease;
}

ion-button[fill="clear"]:hover {
  opacity: 1;
}

/* Override for Forgot Password and Back to Login buttons */
.ion-text-center ion-button[fill="clear"] {
  --color: #000000;
}

.disclaimer-text {
  color: #666666;
  font-size: 14px;
  margin-top: 8px;
}

/* Update password reset form styles to match light mode */
.password-reset-form h2 {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #000000;
}

.password-reset-form p {
  font-size: 16px;
  line-height: 1.4;
  margin-bottom: 24px;
  color: rgba(0, 0, 0, 0.8);
}

.password-reset-form ion-item {
  --background: #F2F2F7;
  border: none !important;
  box-shadow: none !important;
}

.password-reset-form ion-input {
  --color: #000000;
  --placeholder-color: #8E8E93;
  --caret-color: #000000 !important;
  --highlight-color: #000000 !important;
}

.password-reset-form ion-input::part(native) {
  caret-color: #000000 !important;
  -webkit-text-fill-color: #000000 !important;
}

/* Remove the media queries for light mode since it's now the default */
/* Light mode styles - keeping this comment for reference */
/* @media (prefers-color-scheme: light) {
  ...styles removed as they're now the default...
} */

/* Responsive styles */
@media (max-width: 480px) {
  .logo {
    width: 150px;
    margin-bottom: 32px;
  }

  form {
    margin-top: 32px;
  }

  .logo-container {
    padding-top: 32px;
  }
}

/* Password Reset Form Styles */
.password-reset-form {
  margin-top: 20px;
  text-align: center;
}

/* Update password reset form styles to match light mode */
</style>

